import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import DestinationBrandlive from '../../../images/brandlive-platform-icon.png';
import TellyCard from '../../../images/telly-card.png';
import Socket from '../../../connection/socket';
import { GreenroomState as GS } from '../../../stores/types/types';
import { APP } from '../../../stores/session-status';
import { CUSTOM_DESTINATION_OPTIONS, RESOLUTION_OPTIONS } from '../../../stores/reducers/broadcasting';
import { CustomDestination } from '../../../stores/reducers/settings';
import * as SettingsActions from '../../../stores/actions/settings';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import { cardBackground, upcomingDateTime } from '../../../utils/utils';
import SelectSmall from '../../general-components/select-small';
import Switch from '../../general-components/switch';
import DestinationCard from '../../general-components/destination-card';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import BrandLiveLogo from '../../general-components/brandlive-logo';
import { Meeting } from '../../../stores/reducers/session';
import { MODAL_TEMPLATES } from '../../../stores/reducers/modal';

interface Props {
	socket: Socket | null;
	meeting: Meeting;
	location: string;
}

export enum StreamSettingLocation {
	MetaMenu = 'meta-menu',
	ShowDetails = 'show-details',
	StreamModal = 'stream-modal',
}

export default function StreamingSettings(props: Props) {
	const {
		socket,
		meeting,
		location,
	} = props;

	const dispatch = useDispatch();

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const session = useSelector((s: GS) => s.SessionReducer.session);
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);
	const useDefaultEndpoint = useSelector((s: GS) => s.SettingsReducer.useDefaultEndpoint);
	const defaultResolution = useSelector((s: GS) => s.SettingsReducer.defaultResolution);
	const customDestinations = useSelector((s: GS) => s.SettingsReducer.customDestinations);
	const selectedCustomDest = useSelector((s: GS) => s.SettingsReducer.selectedCustomDestination);
	const useCustomEndpoint = useSelector((s: GS) => s.BroadcastReducer.useCustomEndpoint);
	const goLiveButtonClicked = useSelector((s: GS) => s.BroadcastReducer.goLiveButtonClicked);
	const broadcastEndpoint = useSelector((s: GS) => s.BroadcastReducer.broadcastEndpoint);
	const addingDestination = useSelector((s: GS) => s.BroadcastReducer.addingDestination);
	const editingDestination = useSelector((s: GS) => s.BroadcastReducer.editingDestination);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const modalTemplate = useSelector((s: GS) => s.ModalReducer.modalTemplate);

	const destinationsRef = useRef<HTMLDivElement>(null);

	const isMetaMenuWithStreamModalOpen = useMemo(
		() => (
			modalOpen && modalTemplate === MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION
			&& location === StreamSettingLocation.MetaMenu
		),
		[location, modalOpen, modalTemplate],
	);

	function toggleDefault() {
		if (!token || !meeting) return;

		// if the default destination is toggled off, turn it on
		if (!useDefaultEndpoint) {
			dispatch(SettingsActions.setUseDefaultEndpoint(true));
			dispatch(SettingsActions.setSelectedBroadcastResolution(defaultResolution));

			// if a custom destination is toggled on, turn it off
			if (useCustomEndpoint) {
				dispatch(BroadcastActions.updateUseCustomBroadcastEndpoint(
					token,
					meeting.meeting,
					false,
				));
				dispatch(SettingsActions.setSelectedCustomDestination(''));
				socket?.sendMessage('custom-endpoint-off-sync', { from: session });
			}
		}
	}

	function toggleCustom(id: string) {
		if (!token || !meeting) return;

		const custom = customDestinations.find(
			(destination: CustomDestination) => id === destination.id,
		);
		if (custom && selectedCustomDest !== id) {
			// if this custom destination is not toggled on, turn it on
			dispatch(BroadcastActions.updateCustomBroadcastEndpoint(
				token,
				meeting.meeting,
				custom.name,
				custom.url,
				custom.stream_key,
			));
			dispatch(SettingsActions.setSelectedBroadcastResolution(custom.resolution));
			dispatch(SettingsActions.setSelectedCustomDestination(id));
			// if the default destination is toggled on, turn it off
			if (useDefaultEndpoint) dispatch(SettingsActions.setUseDefaultEndpoint(false));
			socket?.sendMessage(
				'custom-endpoint-on-sync',
				{
					name: custom.name,
					url: custom.url,
					stream_key: custom.stream_key,
					from: session,
				},
			);
		}
	}

	function changeDefaultResolution(resolution: string) {
		// if the default destination is currently toggled on, update the selected resolution
		if (useDefaultEndpoint) dispatch(SettingsActions.setSelectedBroadcastResolution(resolution));
		// Needs to be called after setting broadcast resolution
		// as this triggers a reload from the database and overwrites otherwise.
		dispatch(SettingsActions.setDefaultResolution(resolution));
	}

	const destinations = useMemo(() => customDestinations.map(
		(destination: CustomDestination, index: number) => (
			<DestinationCard
				key={`${index}-${destination.id}`}
				{...destination}
				destinationType={
					CUSTOM_DESTINATION_OPTIONS.find((option) => option.value === destination.type)
					|| CUSTOM_DESTINATION_OPTIONS[0]
				}
				socket={socket}
				editingDestination={editingDestination === destination.id && !isMetaMenuWithStreamModalOpen}
				selected={selectedCustomDest}
				meeting={meeting}
				goLiveButtonClicked={goLiveButtonClicked}
				handleEditDestination={(dest: string) => {
					dispatch(BroadcastActions.setEditingDestination(dest));
				}}
				toggleOn={toggleCustom}
				location={location}
			/>
		),
	), [
		customDestinations,
		editingDestination,
		useDefaultEndpoint,
		useCustomEndpoint,
		selectedCustomDest,
		goLiveButtonClicked,
		isMetaMenuWithStreamModalOpen,
	]);

	return (
		<>
			{location === StreamSettingLocation.ShowDetails && appSignedInWith !== APP.GREENROOM && (
				<div className={classNames('main-destination', { disabled: goLiveButtonClicked })}>
					<div className="destination-header">
						<div className="destination-label">
							<img src={DestinationBrandlive} alt={DestinationBrandlive} />
							<BrandLiveLogo height={18} width={50} marginTop={-1} />
						</div>
						<div className="destination-controls">
							<Switch
								on={useDefaultEndpoint}
								onChange={toggleDefault}
								className={'green'}
							/>
						</div>
					</div>
					<div className="main-destination-meta">
						<div className="main-destination-image" style={cardBackground(meeting, TellyCard, false, true)} />
						<div className="main-destination-info">
							<label>
								{upcomingDateTime(
									new Date(meeting.start_time).getTime(),
									new Date(meeting.end_time).getTime(),
									true,
									true,
								)}
							</label>
							<span>{meeting.title}</span>
							<div className="destination-status">
								<Icon name={ICONS.VIEWERS_EYE} color={COLORS.GRAY} size={11} />
								<label>Broadcast</label>
							</div>
						</div>
						<div className="default-destination-video">
							<div className="input-container main">
								<div className="input-header">
									<span>Video Quality</span>
								</div>
								<div className="input-body">
									<SelectSmall
										options={RESOLUTION_OPTIONS}
										onChange={changeDefaultResolution}
										label={defaultResolution}
										selected={
											RESOLUTION_OPTIONS.find(
												(option) => option.value === defaultResolution,
											) || RESOLUTION_OPTIONS[0]
										}
										optionHeight={32}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			<div className="custom-destinations" ref={destinationsRef}>
				{location !== StreamSettingLocation.ShowDetails && appSignedInWith !== APP.GREENROOM && (
					<DestinationCard
						id={'Default'}
						name={CUSTOM_DESTINATION_OPTIONS[0].label}
						url={broadcastEndpoint?.url ?? ''}
						stream_key={broadcastEndpoint?.stream_key ?? ''}
						resolution={defaultResolution}
						logo={CUSTOM_DESTINATION_OPTIONS[0].description ?? ''}
						type={CUSTOM_DESTINATION_OPTIONS[0].value}
						destinationType={CUSTOM_DESTINATION_OPTIONS[0]}
						socket={socket}
						editingDestination={editingDestination === 'Default' && !isMetaMenuWithStreamModalOpen}
						selected={selectedCustomDest}
						meeting={meeting}
						goLiveButtonClicked={goLiveButtonClicked}
						handleEditDestination={(dest: string) => {
							dispatch(BroadcastActions.setEditingDestination(dest));
						}}
						toggleOn={toggleDefault}
						location={location}
					/>
				)}
				{destinations}
				{addingDestination && !isMetaMenuWithStreamModalOpen && (
					<DestinationCard
						id={addingDestination.id}
						name={addingDestination.name}
						url={''}
						stream_key={''}
						resolution={RESOLUTION_OPTIONS[0].value}
						logo={addingDestination.logo}
						type={addingDestination.type}
						destinationType={
							CUSTOM_DESTINATION_OPTIONS.find((option) => option.value === addingDestination.type)
							|| CUSTOM_DESTINATION_OPTIONS[0]
						}
						socket={socket}
						editingDestination={!editingDestination}
						selected={selectedCustomDest}
						meeting={meeting}
						goLiveButtonClicked={goLiveButtonClicked}
						handleEditDestination={() => {
							dispatch(BroadcastActions.setAddingDestination(null));
						}}
						toggleOn={toggleCustom}
						location={location}
					/>
				)}
			</div>
		</>
	);
}
